<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import StatCard from '@/Components/UI/StatCard.vue'
import { tourCategories } from '@/data/content.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  stats:          { type: Object, default: () => ({}) },
  pending_tours:  { type: Array,  default: () => [] },
  recent_vendors: { type: Array,  default: () => [] },
})

const page = usePage()
const firstName = computed(() => page.props.auth?.user?.first_name || '')

const now = ref(new Date())
let timer = null
onMounted(() => { timer = setInterval(() => { now.value = new Date() }, 30000) })
onBeforeUnmount(() => { if (timer) clearInterval(timer) })
const dateLabel = computed(() =>
  now.value.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
)

function catLabel(c) { return tourCategories[c] || c }
function fromNow(iso) {
  if (!iso) return ''
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}
const pendingCount = computed(() => props.stats?.pending_approvals ?? 0)
</script>

<template>
  <div class="dash">
    <header class="dash__head">
      <div>
        <h1 class="dash__title">Welcome back{{ firstName ? `, ${firstName}` : '' }}</h1>
        <p class="dash__date">{{ dateLabel }}</p>
      </div>
      <Link href="/tour_vendors/new" class="dash__cta">
        <span aria-hidden="true">+</span> New tour vendor
      </Link>
    </header>

    <div class="dash__stats">
      <StatCard
        label="Awaiting approval" :value="pendingCount" unit="tours pending review"
        live href="/admin/tour_approvals" :pulse="pendingCount > 0"
      />
      <StatCard label="Live experiences" :value="stats.active_tours ?? 0" unit="approved & active" live href="/tour_vendors" />
      <StatCard label="Tour vendors" :value="stats.tour_vendors ?? 0" unit="onboarded" href="/tour_vendors" />
      <StatCard label="Inbox" :value="stats.inbox_conversations ?? 0" unit="conversations" live href="/inbox" />
    </div>

    <div class="dash__cols">
      <section class="panel">
        <div class="panel__head">
          <h2 class="panel__title">Awaiting your approval</h2>
          <Link href="/admin/tour_approvals" class="panel__more">Review all →</Link>
        </div>
        <ul v-if="pending_tours.length" class="rows">
          <li v-for="t in pending_tours" :key="t.id" class="row">
            <span class="row__dot" aria-hidden="true"></span>
            <div class="row__main">
              <span class="row__title">{{ t.title }}</span>
              <span class="row__sub">{{ t.vendor }} · {{ catLabel(t.category) }} · {{ fromNow(t.submitted_at) }}</span>
            </div>
            <span class="row__price">{{ t.price }}</span>
          </li>
        </ul>
        <div v-else class="empty">
          <p class="empty__title">Nothing waiting</p>
          <p class="empty__sub">Tours submitted by vendors land here for review before they go live on kenya2go.com.</p>
        </div>
      </section>

      <section class="panel">
        <div class="panel__head">
          <h2 class="panel__title">Tour vendors</h2>
          <Link href="/tour_vendors" class="panel__more">All →</Link>
        </div>
        <ul v-if="recent_vendors.length" class="rows">
          <li v-for="v in recent_vendors" :key="v.id" class="row">
            <div class="row__main">
              <Link :href="`/tour_vendors/${v.id}`" class="row__title row__title--link">{{ v.name }}</Link>
              <span class="row__sub">{{ v.tours_count }} {{ v.tours_count === 1 ? 'tour' : 'tours' }}</span>
            </div>
          </li>
        </ul>
        <div v-else class="empty">
          <p class="empty__title">No vendors yet</p>
          <Link href="/tour_vendors/new" class="empty__cta">+ Add your first vendor</Link>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.dash { display: flex; flex-direction: column; gap: 1.5rem; }
.dash__head { display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.dash__title { font-family: var(--display); font-weight: 500; font-size: 1.6rem; letter-spacing: -0.01em; color: var(--ink); margin: 0; }
.dash__date { color: var(--muted); margin: 0.2rem 0 0; font-size: 0.9rem; }
.dash__cta {
  display: inline-flex; align-items: center; gap: 0.4rem;
  background: var(--accent); color: var(--accentInk);
  padding: 0.6rem 1.1rem; border-radius: var(--radius-button, 10px);
  font-weight: 600; font-size: 0.9rem; text-decoration: none;
}
.dash__cta:hover { filter: brightness(0.96); }

.dash__stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; }
@media (max-width: 900px) { .dash__stats { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .dash__stats { grid-template-columns: 1fr; } }

.dash__cols { display: grid; grid-template-columns: 1.5fr 1fr; gap: 1.25rem; align-items: start; }
@media (max-width: 900px) { .dash__cols { grid-template-columns: 1fr; } }

.panel { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-lg); padding: 1.1rem 1.25rem 1.25rem; }
.panel__head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-bottom: 0.9rem; }
.panel__title { font-family: var(--display); font-weight: 500; font-size: 1.15rem; color: var(--ink); margin: 0; }
.panel__more { color: var(--inkSoft); text-decoration: none; font-size: 0.85rem; font-weight: 600; }
.panel__more:hover { color: var(--accent); }

.rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
.row { display: flex; align-items: center; gap: 0.75rem; padding: 0.7rem 0; border-top: 1px solid var(--line); }
.row:first-child { border-top: 0; }
.row__dot { width: 0.5rem; height: 0.5rem; border-radius: 999px; background: var(--warn); flex-shrink: 0; }
.row__main { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; flex: 1; }
.row__title { color: var(--ink); font-weight: 600; font-size: 0.94rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.row__title--link { text-decoration: none; }
.row__title--link:hover { color: var(--accent); }
.row__sub { color: var(--muted); font-size: 0.8rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.row__price { color: var(--accent); font-weight: 600; font-size: 0.9rem; white-space: nowrap; }

.empty { padding: 1.6rem 0.5rem; text-align: center; }
.empty__title { font-weight: 600; color: var(--ink); margin: 0 0 0.3rem; }
.empty__sub { color: var(--muted); font-size: 0.875rem; line-height: 1.5; margin: 0; max-width: 28rem; margin-inline: auto; }
.empty__cta { display: inline-block; margin-top: 0.5rem; color: var(--accent); font-weight: 600; text-decoration: none; }
</style>
